import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle, Shield, KeyRound } from "lucide-react";

const ForgotPassword = () => {
  const { API } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ email });
      const response = await fetch(`${API}/auth/forgot-password?${params.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        setSent(true);
        toast.success("Reset link sent! Check your email.");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex animate-fade-in">
      {/* Left Side - Brand Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero" />
        <img
          src="https://images.unsplash.com/photo-1659353888477-6e6aab941b55?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Healthcare technology"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
              <span className="text-white font-heading font-bold text-2xl">R</span>
            </div>
            <span className="font-heading font-semibold text-xl">RNB Consulting</span>
          </Link>
          
          <div className="space-y-8">
            <div>
              <h2 className="font-heading text-4xl font-bold mb-4 leading-tight">
                Account Recovery
              </h2>
              <p className="text-lg text-white/80 max-w-md">
                Don't worry, it happens to the best of us. We'll help you get back into your account quickly and securely.
              </p>
            </div>
            
            <div className="p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-rnb-teal/20 flex items-center justify-center">
                  <KeyRound className="h-6 w-6 text-rnb-teal" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Quick Recovery</h3>
                  <p className="text-white/70 text-sm">Usually takes less than 2 minutes</p>
                </div>
              </div>
              <p className="text-white/80 text-sm">
                We'll send a secure link to your email that expires in 1 hour for your protection.
              </p>
            </div>
          </div>

          <p className="text-sm text-white/60">
            Need help? Contact support@rnbconsultinggroup.com
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-subtle">
        <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-rnb-blue/5 rounded-full blur-3xl translate-y-1/2" />
        
        <div className="relative z-10 w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="flex lg:hidden items-center justify-center space-x-2 mb-8">
            <div className="w-12 h-12 bg-rnb-blue rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-2xl">R</span>
            </div>
            <span className="font-heading font-semibold text-2xl text-rnb-navy">RNB Consulting</span>
          </Link>

          <div className="card-modern p-8" data-testid="forgot-password-card">
            {sent ? (
              <div className="text-center space-y-6">
                <div className="w-20 h-20 bg-rnb-teal/10 rounded-full flex items-center justify-center mx-auto animate-pulse-glow">
                  <CheckCircle className="h-10 w-10 text-rnb-teal" />
                </div>
                
                <div>
                  <h1 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
                    Check Your Email
                  </h1>
                  <p className="text-rnb-gray">
                    We've sent password reset instructions to
                  </p>
                  <p className="font-semibold text-rnb-navy mt-1">{email}</p>
                </div>

                <div className="p-4 bg-rnb-light rounded-xl">
                  <p className="text-sm text-rnb-gray">
                    Didn't receive the email? Check your spam folder or{" "}
                    <button
                      onClick={() => {
                        setSent(false);
                        setEmail("");
                      }}
                      className="text-rnb-blue font-semibold hover:underline"
                    >
                      try a different email
                    </button>
                  </p>
                </div>

                <Link to="/login">
                  <Button variant="outline" className="w-full h-12">
                    <ArrowLeft className="h-5 w-5 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-rnb-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <KeyRound className="h-8 w-8 text-rnb-blue" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
                    Forgot Password?
                  </h1>
                  <p className="text-rnb-gray">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-rnb-navy font-medium">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-rnb-gray" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="name@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-11 h-12 border-gray-200 focus:border-rnb-teal focus:ring-rnb-teal/20"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 btn-primary text-base font-semibold"
                    disabled={loading}
                    data-testid="send-reset-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>

                  <Link to="/login" className="block">
                    <Button variant="ghost" className="w-full h-12" type="button">
                      <ArrowLeft className="h-5 w-5 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </form>
              </>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-rnb-gray">
            <Shield className="h-4 w-4" />
            <span>Your data is protected</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
